import cn from "classnames";

import { Text } from "@/components/text/text.component";

import { FaqSection as FaqSectionType } from "@/sanity.types";

import { FaqAccordionList } from "./faqAccordionList.component";

export const FaqSection = ({ block, pageType }: FaqSectionProps) => {
  if (!block?.faqs || block?.faqs?.length === 0) return null;

  const layout = (block as FaqSectionBlock).layout ?? "standard";
  const isCenteredLayout = layout === "centered";
  const isMonitoringPage = pageType === "monitoringPage";

  return (
    <section
      aria-label="Frequently asked questions"
      className={cn(
        "flex w-full flex-col",
        {
          "px-5 py-10 md:px-0": !isCenteredLayout,
          "bg-primary text-greyscale-body":
            !isCenteredLayout && block.theme === "primary",
          "bg-secondary text-white":
            !isCenteredLayout && block.theme === "secondary",
          "bg-greyscale-background text-greyscale-body":
            !isCenteredLayout && block.theme === "light",
          "bg-greyscale-background text-greyscale-body px-6 py-8 md:px-10 md:py-12 lg:py-14":
            isCenteredLayout,
        },
        !isCenteredLayout && block.theme,
      )}
      data-testid="faq-section">
      <div
        className={cn("mx-auto w-full", {
          container: !isCenteredLayout,
          "max-w-[1090px]": isCenteredLayout,
        })}>
        <Text
          variant={isCenteredLayout ? "preHeaderSm" : "preHeaderLg"}
          className={cn("text-center uppercase", {
            "text-greyscale-headline tracking-[0.02em]": isCenteredLayout,
            "text-greyscale-headline": isMonitoringPage && !isCenteredLayout,
          })}
          id="faq-section-title">
          {block.title}
        </Text>
        {block.subtitle && (
          <Text
            variant={isCenteredLayout ? "h5" : "h3"}
            component={isCenteredLayout ? "h2" : "h3"}
            id="faq-section-subtitle"
            className={cn("mt-2 text-center", {
              "text-greyscale-headline mx-auto max-w-[760px]": isCenteredLayout,
              "text-greyscale-headline": isMonitoringPage && !isCenteredLayout,
            })}>
            {block.subtitle}
          </Text>
        )}
        <div
          className={cn("flex flex-col", {
            "mt-12": !isCenteredLayout,
            "mt-7 md:mt-9": isCenteredLayout,
          })}>
          <FaqAccordionList
            faqs={block.faqs}
            theme={block.theme}
            isCenteredLayout={isCenteredLayout}
            isMonitoringPage={isMonitoringPage}
          />
        </div>
      </div>
    </section>
  );
};

type FaqSectionProps = {
  block: FaqSectionType;
  pageType?: string;
};

type FaqSectionBlock = FaqSectionType & {
  layout?: "standard" | "centered";
};
